import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import ChatBox from './ChatBox';
import '../css/BusinessProfile.css';

const API_URL = process.env.REACT_APP_API_URL;

const BusinessProfile = () => {
  const [formData, setFormData] = useState({
    businessName: '',
    fields: [],
  });
  const [reservations, setReservations] = useState([]);
  const [dailySlots, setDailySlots] = useState([]);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [isUpdating, setIsUpdating] = useState(false);
  const [isActive, setIsActive] = useState(true);
  const [showChatBox, setShowChatBox] = useState(false);
  const [receiverId, setReceiverId] = useState(null);
  const [receiverModel, setReceiverModel] = useState('User');
  const [photoFile, setPhotoFile] = useState(null);
  const [passwordData, setPasswordData] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  });
  const navigate = useNavigate();

  const fetchProfile = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        navigate('/login');
        return;
      }
      const response = await fetch(`${API_URL}/api/profile/business`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.message || 'Profil alınamadı.');
      setIsActive(data.isActive);
      setFormData({
        businessName: data.businessName || '',
        fields: data.fields || [],
      });
    } catch (error) {
      console.error('Profil alınamadı:', error);
    }
  }, [navigate]);

  const toggleUpdateSection = () => setIsUpdating(!isUpdating);

  const handleFieldChange = (index, key, value) => {
    const updatedFields = [...formData.fields];
    updatedFields[index][key] = value;
    setFormData({ ...formData, fields: updatedFields });
  };

  // ⏰ Saatleri özel sıralama fonksiyonu (00:00–06:00 arası en sona)
  const sortWorkingHours = (hours) => {
    return hours.sort((a, b) => {
      const getTimeValue = (t) => {
        if (!t) return 0;
        const [h, m] = t.split(':').map(Number);
        const total = h * 60 + m;
        return h < 6 ? total + 24 * 60 : total;
      };
      return getTimeValue(a.start) - getTimeValue(b.start);
    });
  };

  // 🔹 Yalnızca input değiştiğinde: imleci sabit tut
  const handleWorkingHoursChange = (fieldIndex, hourIndex, key, value) => {
    const updatedFields = [...formData.fields];
    if (!updatedFields[fieldIndex].workingHours) {
      updatedFields[fieldIndex].workingHours = [];
    }
    if (!updatedFields[fieldIndex].workingHours[hourIndex]) {
      updatedFields[fieldIndex].workingHours[hourIndex] = { start: '', end: '' };
    }
    updatedFields[fieldIndex].workingHours[hourIndex][key] = value;
    setFormData({ ...formData, fields: updatedFields });
  };

  // 🔹 Kullanıcı input'tan çıkınca sıralama uygula
  const handleWorkingHourBlur = (fieldIndex) => {
    const updatedFields = [...formData.fields];
    updatedFields[fieldIndex].workingHours = sortWorkingHours(
      updatedFields[fieldIndex].workingHours
    );
    setFormData({ ...formData, fields: updatedFields });
  };

  const addWorkingHour = (fieldIndex) => {
    const updatedFields = [...formData.fields];
    updatedFields[fieldIndex].workingHours = updatedFields[fieldIndex].workingHours || [];
    updatedFields[fieldIndex].workingHours.push({ start: '', end: '' });
    setFormData({ ...formData, fields: updatedFields });
  };

  const removeWorkingHour = (fieldIndex, hourIndex) => {
    const updatedFields = [...formData.fields];
    updatedFields[fieldIndex].workingHours.splice(hourIndex, 1);
    setFormData({ ...formData, fields: updatedFields });
  };

  const fetchReservations = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        navigate('/login');
        return;
      }
      const response = await fetch(
        `${API_URL}/api/reservations/business-reservations?businessId=${localStorage.getItem('businessId')}`,
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
      );
      const data = await response.json();
      if (response.ok) setReservations(data.reservations);
    } catch (error) {
      console.error('Rezervasyonlar alınamadı:', error);
    }
  }, [navigate]);

  const fetchDailySlots = useCallback(async () => {
    const token = localStorage.getItem('token');
    const businessId = localStorage.getItem('businessId');
    if (!token || !businessId) return navigate('/login');
    const isoDate = currentDate.toISOString().split('T')[0];
    const res = await fetch(
      `${API_URL}/api/reservations/daily?businessId=${businessId}&date=${isoDate}`,
      { headers: { Authorization: `Bearer ${token}` } }
    );
    const data = await res.json();
    if (res.ok) setDailySlots(data.dailyData || []);
  }, [currentDate, navigate]);

  useEffect(() => {
    if (!localStorage.getItem('businessId')) {
      console.error('Business ID bulunamadı.');
      navigate('/login');
      return;
    }
    fetchProfile();
    fetchReservations();
    fetchDailySlots();
  }, [fetchProfile, fetchReservations, fetchDailySlots]);

  const formatDate = (dateString) => {
    if (!dateString) return 'Geçersiz Tarih';
    const options = { weekday: 'long', day: '2-digit', month: '2-digit', year: 'numeric' };
    const date = new Date(dateString);
    return date.toLocaleDateString('tr-TR', options);
  };

  const handleDateChange = (days) => {
    const newDate = new Date(currentDate);
    newDate.setDate(newDate.getDate() + days);
    setCurrentDate(newDate);
  };

  const handleApprove = async (reservationId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/reservations/approve`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ reservationId }),
      });
      if (response.ok) {
        alert('Rezervasyon onaylandı!');
        fetchReservations();
        fetchDailySlots();
      } else {
        const data = await response.json();
        alert(data.message || 'Rezervasyon onaylanamadı.');
      }
    } catch (error) {
      console.error('Rezervasyon onaylanamadı:', error);
    }
  };

  const handleReject = async (reservationId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/reservations/reject`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ reservationId }),
      });
      if (response.ok) {
        alert('Rezervasyon reddedildi!');
        fetchReservations();
        fetchDailySlots();
      } else {
        const data = await response.json();
        alert(data.message || 'Rezervasyon reddedilemedi.');
      }
    } catch (error) {
      console.error('Rezervasyon reddedilemedi:', error);
    }
  };

  const handleDelete = async (reservationId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/reservations/${reservationId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) {
        alert('Rezervasyon silindi.');
        fetchReservations();
        fetchDailySlots();
      } else {
        const data = await response.json();
        alert(data.message || 'Silme başarısız.');
      }
    } catch (error) {
      console.error('Silme hatası:', error);
    }
  };

  const uploadPhoto = async () => {
    if (!photoFile) return;

    const formData = new FormData();
    formData.append("photo", photoFile);

    try {
      const res = await fetch(`${API_URL}/api/business/upload-photo`, {
        method: "PUT",
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
        body: formData,
      });

      const data = await res.json();
      if (res.ok) {
        alert("Fotoğraf güncellendi!");
        fetchProfile();
      } else {
        alert(data.message || "Fotoğraf yüklenemedi");
      }
    } catch (err) {
      console.error(err);
    }
};

const handlePasswordUpdate = async () => {
  if (passwordData.newPassword !== passwordData.confirmPassword) {
    alert("Yeni şifreler uyuşmuyor!");
    return;
  }

  const res = await fetch(`${API_URL}/api/business/change-password`, {
    method: "PUT",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${localStorage.getItem("token")}`,
    },
    body: JSON.stringify(passwordData),
  });

  const data = await res.json();

  if (res.ok) {
    alert("Şifre güncellendi!");
    setPasswordData({
      currentPassword: "",
      newPassword: "",
      confirmPassword: "",
    });
  } else {
    alert(data.message || "Şifre değiştirilemedi");
  }
};


  const handleSubmit = async (e) => {
  e.preventDefault();
  if (photoFile) {
    await uploadPhoto();
  }

  // 🕒 Güncelleme sırasında tüm saha saatlerini sırala
  const sortedFields = formData.fields.map((field) => ({
    ...field,
    workingHours: field.workingHours ? sortWorkingHours([...field.workingHours]) : [],
  }));

  const updatedData = { ...formData, fields: sortedFields };
  setFormData(updatedData);

  try {
    const response = await fetch(`${API_URL}/api/business/update`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${localStorage.getItem('token')}`,
      },
      body: JSON.stringify(updatedData),
    });

    if (response.ok) {
      alert('Profil başarıyla güncellendi ve saatler sıralandı!');
    } else {
      console.error('Profil güncellenemedi.');
    }
  } catch (error) {
    console.error('Profil güncellenemedi:', error);
  }
};


  return (
    <div className="business-profile-container">
      <div className="profile-header">
        <h2>İşletme Profilimi Güncelle</h2>
        <button className="toggle-button" onClick={toggleUpdateSection}>
          {isUpdating ? '▲' : '▼'}
        </button>
      </div>

      {isUpdating && (
        <form className="update-form" onSubmit={handleSubmit}>
          <div className="photo-upload mb-3">
            <label className="field-label fw-bold">Profil Fotoğrafı</label>
            <input
              type="file"
              accept="image/*"
              className="form-control"
              onChange={(e) => setPhotoFile(e.target.files[0])}
            />
          </div>
          {formData.fields.map((field, index) => (
            <div key={index} className="field-group">
              <label className="field-label">
                Saha Adı:
                <input
                  type="text"
                  className="field-input"
                  value={field.name}
                  onChange={(e) => handleFieldChange(index, 'name', e.target.value)}
                />
              </label>
              <label className="field-label">
                Kapasite:
                <input
                  type="text"
                  className="field-input"
                  value={field.capacity}
                  onChange={(e) => handleFieldChange(index, 'capacity', e.target.value)}
                />
              </label>
              <label className="field-label">
                Fiyat:
                <input
                  type="number"
                  className="field-input"
                  value={field.price}
                  onChange={(e) => handleFieldChange(index, 'price', e.target.value)}
                />
              </label>

              <div className="working-hours">
                {field.workingHours.map((hour, hourIndex) => (
                  <div key={hourIndex} className="working-hour-group">
                    <label className="working-hour-label">
                      Başlangıç Saati:
                      <input
                        type="text"
                        className="working-hour-input"
                        value={hour.start || ''}
                        onChange={(e) =>
                          handleWorkingHoursChange(index, hourIndex, 'start', e.target.value)
                        }
                      />
                    </label>
                    <label className="working-hour-label">
                      Bitiş Saati:
                      <input
                        type="text"
                        className="working-hour-input"
                        value={hour.end || ''}
                        onChange={(e) =>
                          handleWorkingHoursChange(index, hourIndex, 'end', e.target.value)
                        }
                        onBlur={() => handleWorkingHourBlur(index)}
                      />
                    </label>
                    <button
                      type="button"
                      className="remove-hour-button"
                      onClick={() => removeWorkingHour(index, hourIndex)}
                    >
                      Sil
                    </button>
                  </div>
                ))}
              </div>

              <button
                type="button"
                className="add-hour-button"
                onClick={() => addWorkingHour(index)}
              >
                Ekle
              </button>
            </div>
          ))}
          <button type="submit" className="update-submit-button">
            Güncelle
          </button>
        <div className="password-update mt-4 p-3 border rounded">
          <h5 className="text-success">Şifre Güncelle</h5>

          <input
            type="password"
            className="form-control my-2"
            placeholder="Mevcut Şifre"
            value={passwordData.currentPassword}
            onChange={(e) =>
              setPasswordData({ ...passwordData, currentPassword: e.target.value })
            }
          />

          <input
            type="password"
            className="form-control my-2"
            placeholder="Yeni Şifre"
            value={passwordData.newPassword}
            onChange={(e) =>
              setPasswordData({ ...passwordData, newPassword: e.target.value })
            }
          />

          <input
            type="password"
            className="form-control my-2"
            placeholder="Yeni Şifre Tekrar"
            value={passwordData.confirmPassword}
            onChange={(e) =>
              setPasswordData({ ...passwordData, confirmPassword: e.target.value })
            }
          />

          <button
            type="button"
            className="btn btn-warning mt-2"
            onClick={handlePasswordUpdate}
          >
            Şifreyi Güncelle
          </button>
        </div>
        </form>
      )}

      <h2 className="text-center my-4 text-success">Günlük Rezervasyonlar</h2>
      <div className="d-flex justify-content-center align-items-center mb-3 gap-2">
        <button className="btn btn-success" onClick={() => handleDateChange(-1)}>
          Önceki Gün
        </button>
        <span className="fw-bold">{formatDate(currentDate.toISOString())}</span>
        <button className="btn btn-success" onClick={() => handleDateChange(1)}>
          Sonraki Gün
        </button>
      </div>

      {dailySlots.length > 0 ? (
        dailySlots.map((field, fieldIndex) => (
          <div key={fieldIndex} className="card mb-4 shadow-sm">
            <div className="card-header bg-success text-white">
              <h5 className="mb-0">
                {field.fieldName} - Kapasite: {field.capacity}
              </h5>
            </div>
            <div className="table-responsive">
              <table className="table table-bordered mb-0">
                <thead className="table-light">
                  <tr>
                    <th scope="col">Saat</th>
                    <th scope="col">Durum</th>
                  </tr>
                </thead>
                <tbody>
                  {field.daySlots.map((slot, slotIndex) => (
                    <tr
                      key={slotIndex}
                      className={
                        slot.status === 'approved'
                          ? 'table-success'
                          : slot.status === 'pending'
                          ? 'table-warning'
                          : ''
                      }
                    >
                      <td>{slot.timeSlot}</td>
                      <td>
                        {slot.status === 'approved' && slot.user?.fullName ? (
                          <>
                            <strong>{slot.user.fullName}</strong>
                            {slot.reservationId && (
                              <button
                                className="btn btn-sm btn-danger ms-2"
                                onClick={() => handleDelete(slot.reservationId)}
                              >
                                🗑️ Sil
                              </button>
                            )}
                          </>
                        ) : slot.status === 'pending' ? (
                          <span className="text-warning fw-bold">Bekliyor</span>
                        ) : (
                          <span className="text-muted">Boş</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ))
      ) : (
        <p className="text-center text-muted">Bugün için veri yok.</p>
      )}

      <div className="reservation-requests-container">
        <h2 className="reservation-requests-title">Rezervasyon İstekleri</h2>
        <p style={{ textAlign: 'center' }}>7 günü geçmiş istekler otomatik olarak silinir</p>
        {reservations.length === 0 ? (
          <p className="no-requests">Henüz rezervasyon isteği yok.</p>
        ) : (
          <ul className="list-group">
            {reservations
              .filter((res) => {
                const resDate = new Date(res.date).toISOString().split('T')[0];
                const currentISO = currentDate.toISOString().split('T')[0];
                return resDate === currentISO;
              })
              .map((res) => (
                <li key={res._id} className="list-group-item mb-3 shadow-sm rounded">
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <h6 className="mb-0 text-success">
                      <i className="bi bi-person-fill me-1"></i>
                      {res.user?.fullName || 'Kullanıcı yok'} ({res.user?.email || 'Email yok'})
                    </h6>
                  </div>
                  <p className="mb-1">
                    <strong>📞 Telefon:</strong> {res.user?.phone || 'Belirtilmemiş'}
                  </p>
                  <p className="mb-1">
                    <strong>🏟 Halı Saha:</strong> {res.fieldName}
                  </p>
                  <p className="mb-1">
                    <strong>📅 Tarih:</strong> {formatDate(res.date)}
                  </p>
                  <p className="mb-1">
                    <strong>🕒 Saat:</strong> {res.timeSlot}
                  </p>
                  <p className="mb-2">
                    <strong>📌 Durum:</strong>{' '}
                    <span
                      className={`badge ${
                        res.status === 'approved'
                          ? 'bg-success'
                          : res.status === 'pending'
                          ? 'bg-warning text-dark'
                          : 'bg-danger'
                      }`}
                    >
                      {res.status}
                    </span>
                  </p>
                  <div className="d-flex flex-wrap gap-2">
                    {res.status === 'pending' && (
                      <>
                        <button
                          className="btn btn-sm btn-outline-success"
                          onClick={() => handleApprove(res._id)}
                        >
                          ✅ Onayla
                        </button>
                        <button
                          className="btn btn-sm btn-outline-danger"
                          onClick={() => handleReject(res._id)}
                        >
                          ❌ Reddet
                        </button>
                      </>
                    )}
                    <button
                      className="btn btn-sm btn-success"
                      onClick={() => {
                        setReceiverId(res.user._id);
                        setReceiverModel('User');
                        setShowChatBox(true);
                      }}
                    >
                      💬 Mesaj Gönder
                    </button>
                  </div>
                </li>
              ))}
          </ul>
        )}
      </div>

      {showChatBox && (
        <ChatBox
          receiverId={receiverId}
          receiverModel={receiverModel}
          onClose={() => setShowChatBox(false)}
        />
      )}
    </div>
  );
};

export default BusinessProfile;
